import React, { useEffect, useMemo, useState } from "react";
import { CustomDropdown } from "../../../../component/customdropdown/CustomDropdown";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  setBulkSparesFilter,
  selectBulkSparesFilterState,
} from "../../../../store/bulk/bulkSparesFilterSlice";

import {
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";

import classes from "./bulkSparesFilterPage.module.css";

import { useUploadBulkSpareFileMutation } from "../../../../services/bulk/spares/bulkSparesApiSlice";
import { toast } from "react-toastify";
import axiosInstance from "../../../../axios/axiosTokenMiddleware";
import {
  selectAllBulkSpareSellers,
  useGetBulkSpareSellerListQuery,
} from "../../../../services/bulk/spares/bulkSpareSellerApiSlice";
import {
  makeSelectBulkSpareLotSelectors,
  useGetBulkSpareLotQuery,
} from "../../../../services/bulk/spares/bulkSpareLotApiSlice";
import { skipToken } from "@reduxjs/toolkit/query";


import { Modal } from "../../../../component/inventory/spares/modal/Modal";
import { useGetBulkSparesPriorityQuery } from "../../../../services/bulk/bulkSparePrioritySlice";
import { useUpdateBulkSparesPriorityMutation } from "../../../../services/bulk/bulkSparePriorityMutationSlice";



const statuses = [];


export const BulkSparesFilterPage = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSellerId, setSelectedSellerId] = useState(null);
  const [selectedLotId, setSelectedLotId] = useState(null);
  const filters = useSelector(selectBulkSparesFilterState);
  useGetBulkSpareSellerListQuery();
  const sellerList = useSelector(selectAllBulkSpareSellers);

  useGetBulkSpareLotQuery(
    selectedSellerId ? { seller_id: selectedSellerId } : skipToken,
  );

  const lotSelectors = useMemo(() => {
    if (!selectedSellerId) return null;
    return makeSelectBulkSpareLotSelectors(selectedSellerId);
  }, [selectedSellerId]);

  // 3️⃣ useSelector with a STABLE selector
  const EMPTY_ARRAY = [];
  const lotList = useSelector(
    lotSelectors ? lotSelectors.selectAll : () => EMPTY_ARRAY,
  );

  const [updatePriority, { isLoading }] = useUpdateBulkSparesPriorityMutation();

  const { data, isSuccess } = useGetBulkSparesPriorityQuery();

  /* ---------- Sync URL → Redux ---------- */
  useEffect(() => {
    const seller_id = searchParams.get("seller_id") || "";
    const status = searchParams.get("status") || "";

    if (seller_id !== filters.seller_id || status !== filters.status) {
      dispatch(setBulkSparesFilter({ seller_id, status }));
    }
  }, [searchParams, filters, dispatch]);

  /* ---------- Update URL ---------- */
  const updateFilterParams = (key, value) => {
    const params = new URLSearchParams(searchParams);

    value ? params.set(key, value) : params.delete(key);

    setSearchParams(params);
  };

  const handleSelection = (key, value) => {
    updateFilterParams(key, value);
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setSelectedSellerId(null);
    setSelectedLotId(null);
  };
  const handleConfirm = async () => {
    if (!selectedSellerId || !selectedLotId) return;

    const toastId = toast.loading("Please wait...");

    try {
      await updatePriority({
        seller_id: selectedSellerId,
        lot_id: selectedLotId,
      }).unwrap();

      toast.update(toastId, {
        render: "Priority set successfully",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });

      // Close modal after success
      setIsModalOpen(false);
      setSelectedSellerId(null);
      setSelectedLotId(null);
    } catch (error) {
      toast.update(toastId, {
        render: error?.data?.detail || "Failed to update priority",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div className={classes.box}>
      {/* Hidden input */}

      <div className={classes.box__content}>
        <CustomDropdown
          id="seller_id"
          label="Select Seller"
          options={sellerList}
          selectedId={Number(filters.seller_id) || ""}
          onSelection={handleSelection}
        />
      </div>

      <div className={classes.box__content}>
        <CustomDropdown
          id="status"
          label="Select Status"
          options={statuses}
          selectedId={filters.status || ""}
          onSelection={handleSelection}
        />
      </div>

      <div className={classes.actions}>
        <button className={classes.box__btn} onClick={handleOpenModal}>
          Set Priority
        </button>
      </div>
      <Modal
        isOpen={isModalOpen}
        title="Prioritize Seller and their lots"
        onClose={handleCloseModal}
      >
        <div className={classes.modal__head}>
          <h2
            className={classes.modal__head__title}
          >{`Seller: ${data?.seller}`}</h2>
          <h2
            className={classes.modal__head__title}
          >{`Lot Id: ${data?.lotId}`}</h2>
        </div>
        <div className={classes.modalContainer}>
          {/* LEFT: Sellers */}
          <div className={classes.sellerList}>
            <div className={classes.listHeader}>Select Seller</div>

            {sellerList
              .filter((seller) => seller.id) // keeps only truthy ids (non-empty, non-null)
              .map((seller) => (
                <div
                  key={seller.id}
                  className={`${classes.chip} ${
                    seller.id === selectedSellerId ? classes.active : ""
                  }`}
                  onClick={() => {
                    setSelectedSellerId(seller.id);
                    setSelectedLotId(null);
                  }}
                >
                  {seller.label}
                </div>
              ))}
          </div>

          {/* RIGHT: Lots */}
          <div className={classes.lotList}>
            <div className={classes.listHeader}>
              {selectedSellerId ? "Select Lot" : "Select seller to see lots"}
            </div>

            {!selectedSellerId && (
              <div className={classes.emptyState}>
                Please select a seller to view lots
              </div>
            )}

            {selectedSellerId && lotList.length === 0 && (
              <div className={classes.emptyState}>
                No lots available for this seller
              </div>
            )}

            {lotList.map((lot) => (
              <div
                key={lot.id}
                className={`${classes.chip} ${
                  lot.id === selectedLotId ? classes.active : ""
                }`}
                onClick={() => setSelectedLotId(lot.id)}
              >
                {lot.label}
              </div>
            ))}
          </div>
        </div>

        <div className={classes.modalFooter}>
          <button
            disabled={!selectedLotId || isLoading}
            onClick={handleConfirm}
          >
            {isLoading ? "Saving..." : "Confirm"}
          </button>
        </div>
      </Modal>
    </div>
  );
};
